import { MissingVarError } from '@execonline-inc/environment';
import { warn } from '@execonline-inc/logging';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { MissingValue, UserDataLayer } from '../../../Analytics/Types';
import { RegistrationStatus, RegistrationType } from '../../../ProgramsStore/Types';
import { AlreadyTranslatedText } from '../../../Translations';

export interface EventLayer {
  event: string;
}

interface AnalyticsEventLayer {
  hitType: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
}

export type DataLayer =
  | UserDataLayer
  | CourseLayer
  | EventLayer
  | AnalyticsEventLayer
  | DiscoveryPortalDataLayer
  | ProgramDetailDataLayer
  | ProgramDataLayer
  | ProgramDetailAndDiscoveryPortalDataLayer
  | DiscoveryPortalSearchDataLayer
  | SearchResultClickDataLayer
  | VideoTitleDataLayer;

// Sent when a learner opens an experience preview video. JW Player then sends
// its own Play, Pause, and other events, and GTM reads videoTitle from the
// data layer for them. GTM used to read the title from the page.
export interface VideoTitleDataLayer extends EventLayer {
  videoTitle: string;
}

// Sent when Discovery Portal search results load. searchResults is the first
// 3 titles joined with '|', the same value GTM used to read from the page.
export interface DiscoveryPortalSearchDataLayer extends EventLayer {
  searchTerm: string;
  searchResultCount: number;
  searchResults: string;
}

// Sent as 'discovery-portal-search-result-click' when a learner clicks a
// Discovery Portal search result. position is 1-based. GTM used to add these
// click listeners in a Custom HTML tag that pushes 'searchResultClick'.
export interface SearchResultClickDataLayer extends EventLayer {
  searchResultPosition: number;
  searchResultTitle: string;
}

export interface DiscoveryPortalDataLayer extends EventLayer {
  learningCollectionId: number;
  learningCollectionName: string;
  productLicenseId: number | 'N/A';
  productLicenseType: string;
  pageTitle: string;
  alreadyEnrolled: boolean;
}

export interface ProgramDetailDataLayer extends EventLayer {
  programFamilyId: number;
  programFamilyName: string;
  schoolPartnerId: number;
  schoolPartnerName: string;
  duration: Maybe<number>;
  alreadyEnrolled: boolean;
}

export interface ProgramDetailAndDiscoveryPortalDataLayer extends EventLayer {
  programFamilyId: number;
  programFamilyName: string;
  schoolPartnerId: number;
  schoolPartnerName: string;
  duration: Maybe<number>;
  alreadyEnrolled: boolean;
  learningCollectionId: number;
  learningCollectionName: string;
  productLicenseId: number | 'N/A';
  productLicenseType: string;
}

interface ProgramDataLayer extends EventLayer {
  studentId: number;
  studentStatus: RegistrationStatus;
  registrationType: RegistrationType;
  programId: number;
  programName: string;
  moduleId: number;
  segmentId: number;
  segmentName: string;
  learningCollectionId: Maybe<number>;
  learningCollectionName: Maybe<AlreadyTranslatedText>;
  productLicenseId: Maybe<number>;
  productLicenseType: Maybe<AlreadyTranslatedText>;
  programFamilyId: Maybe<number>;
  programFamilyName: Maybe<AlreadyTranslatedText>;
  schoolPartnerId: Maybe<number>;
  schoolPartnerName: Maybe<AlreadyTranslatedText>;
  enrollmentOpenDate: Maybe<Date>;
  duration: Maybe<number>;
  percentComplete: number;
  percentCompleteBucket: PercentCompleteBucket;
  startsOn: Maybe<Date>;
  endsOn: Maybe<Date>;
  programTitle: string;
  programCreatedOn: Maybe<Date>;
}

export type PercentCompleteBucket = '100' | '90' | '75' | '50' | '25' | '10' | 'below_10';

// The GTM Program Progress tag sends programProgress_<bucket>. This is the
// same bucketing that its Custom JavaScript variable did in the browser.
export const percentCompleteBucket = (percent: number): PercentCompleteBucket => {
  if (percent === 100) return '100';
  if (percent >= 90) return '90';
  if (percent >= 75) return '75';
  if (percent >= 50) return '50';
  if (percent >= 25) return '25';
  if (percent >= 10) return '10';
  return 'below_10';
};

interface CourseLayer {
  course: string;
  userType: string;
}

export interface GoogleTagManager {
  dataLayerFn: DataLayerFn;
}

export interface AnalyticsEvent {
  hitType: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
}

interface DataLayerFn {
  set: (key: string, value: string) => void;
}

export interface GtmDataLayer {
  dataLayer: DataLayer[];
}

interface MissingGtmAPI {
  kind: 'missing-gtm-api';
}

export type IdentifyToGtmErrors = MissingGtmAPI | MissingVarError | MissingValue;

export type GtmInitializationError = MissingVarError | GtmLoadError;

export interface GtmLoadError {
  kind: 'gtm-load-error';
  key: string;
}

export const gtmLoadError = (key: string): GtmLoadError => ({
  kind: 'gtm-load-error',
  key,
});

export const missingGtmAPI = (): MissingGtmAPI => ({ kind: 'missing-gtm-api' });

export const handleProgramUpdateError =
  (title: string) =>
  (error: IdentifyToGtmErrors): void => {
    switch (error.kind) {
      case 'missing-gtm-api':
      case 'missing-var-error':
        warn("Google tag manager isn't configured properly.", title);
        break;
      case 'missing-value':
        warn(`Google tag manager cannot initialize without value for ${error.name}`);
        break;
      default:
        assertNever(error);
    }
  };
